import React, { useEffect, useRef, useState } from 'react';
import { 
  FaBriefcase, FaCheckCircle, FaUsers, FaChartLine, FaShieldAlt, 
  FaRocket, FaCogs, FaTools, FaSync, FaCalendarAlt, FaMapMarkerAlt,
  FaStar, FaCertificate, FaMedal, FaAward, FaLightbulb, FaHandshake,
  FaSun, FaMoon
} from 'react-icons/fa';
import { SiWordpress, SiLinux, SiAws, SiDocker } from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Experience = () => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeStat, setActiveStat] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-timeline-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const experience = {
    role: "Associate Tech Operations",
    company: "UnifyCx",
    location: "Mysore, Karnataka",
    duration: "2023 – Present",
    timeline: [
      { year: "2023", month: "Jun", event: "Joined as Tech Operations Associate" },
      { year: "2023", month: "Dec", event: "Promoted to Associate Tech Operations" },
      { year: "2024", month: "Jun", event: "Certified in Advanced Troubleshooting" },
      { year: "2024", month: "Nov", event: "Led Critical Incident Response Team" },
    ],
    responsibilities: [
      {
        icon: <FaCheckCircle className="h-6 w-6" />,
        title: "Production Issue Resolution",
        text: "Resolved 10,000+ WordPress production issues related to performance, SSL, plugin conflicts, and site migrations",
        color: "from-green-500 to-emerald-500",
        darkColor: "from-green-400 to-emerald-400",
        metrics: ["10,000+", "Issues", "Resolved"]
      },
      {
        icon: <FaUsers className="h-6 w-6" />,
        title: "Cross-functional Collaboration",
        text: "Collaborated with cross-functional teams to provide technical assistance and root cause analysis for critical escalations",
        color: "from-blue-500 to-cyan-500",
        darkColor: "from-blue-400 to-cyan-400",
        metrics: ["100+", "Teams", "Supported"]
      },
      {
        icon: <FaShieldAlt className="h-6 w-6" />,
        title: "Infrastructure Management",
        text: "Managed Linux-based hosting environments, DNS configurations, and server-level setups for production websites",
        color: "from-purple-500 to-pink-500",
        darkColor: "from-purple-400 to-pink-400",
        metrics: ["500+", "Servers", "Managed"]
      },
      {
        icon: <FaChartLine className="h-6 w-6" />,
        title: "Performance Optimization",
        text: "Handled 30–40 support tickets per shift with 99.9% SLA compliance and optimized response times by 40%",
        color: "from-orange-500 to-red-500",
        darkColor: "from-orange-400 to-red-400",
        metrics: ["99.9%", "SLA", "Compliance"]
      }
    ],
    technologies: [
      { icon: <SiWordpress className="h-5 w-5" />, name: "WordPress", color: "text-blue-600 dark:text-blue-400", bgColor: "bg-blue-100 dark:bg-blue-900/30" },
      { icon: <SiLinux className="h-5 w-5" />, name: "Linux", color: "text-yellow-600 dark:text-yellow-400", bgColor: "bg-yellow-100 dark:bg-yellow-900/30" },
      { icon: <FaTools className="h-5 w-5" />, name: "Troubleshooting", color: "text-green-600 dark:text-green-400", bgColor: "bg-green-100 dark:bg-green-900/30" },
      { icon: <FaSync className="h-5 w-5" />, name: "CI/CD", color: "text-purple-600 dark:text-purple-400", bgColor: "bg-purple-100 dark:bg-purple-900/30" },
      { icon: <FaAward className="h-5 w-5" />, name: "AWS Basics", color: "text-orange-500 dark:text-orange-400", bgColor: "bg-orange-100 dark:bg-orange-900/30" },
      { icon: <SiDocker className="h-5 w-5" />, name: "Docker", color: "text-blue-500 dark:text-blue-400", bgColor: "bg-blue-100 dark:bg-blue-900/30" },
    ],
    achievements: [
      "Reduced average ticket resolution time by 30% through process optimization",
      "Achieved 100% SLA compliance for critical priority tickets",
      "Mentored 3 junior team members in production troubleshooting",
      "Implemented automation scripts reducing manual work by 25%"
    ]
  };

  const stats = [
    { value: '10,000+', label: 'Issues Resolved', icon: <FaCheckCircle />, color: 'from-green-500 to-emerald-500', darkColor: 'from-green-400 to-emerald-400' },
    { value: '40+', label: 'Tickets/Day', icon: <FaChartLine />, color: 'from-blue-500 to-cyan-500', darkColor: 'from-blue-400 to-cyan-400' },
    { value: '99.9%', label: 'SLA Compliance', icon: <FaShieldAlt />, color: 'from-purple-500 to-pink-500', darkColor: 'from-purple-400 to-pink-400' },
    { value: '24/7', label: 'Production Support', icon: <FaSync />, color: 'from-orange-500 to-red-500', darkColor: 'from-orange-400 to-red-400' },
  ];

  return (
    <section 
      id="experience" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      {/* Theme Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-500/5 to-cyan-500/5' 
            : 'bg-gradient-to-r from-blue-300/10 to-cyan-300/10'
        }`}></div>
        <div className={`absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5' 
            : 'bg-gradient-to-r from-purple-300/10 to-pink-300/10'
        }`}></div>
        
        {/* Animated timeline dots */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full animate-pulse ${
              darkMode ? 'bg-blue-400/20' : 'bg-blue-400/30'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${1.5 + Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaBriefcase className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">💼</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Professional </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Journey
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'Tech Operations Specialist',
                2000,
                'Production Support Expert',
                2000,
                'Infrastructure Engineer',
                2000,
                'DevOps Practitioner',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Main Experience Card */}
        <div className="relative mb-16">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-600 rounded-3xl blur opacity-20 animate-pulse"></div>
          
          <div className={`relative rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm border ${
            darkMode 
              ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700/50' 
              : 'bg-gradient-to-br from-white to-gray-50 border-white/50'
          }`}>
            {/* Header Section */}
            <div className="relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-500"></div>
              
              <div className="p-8 md:p-10">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                        <FaBriefcase className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className={`text-2xl md:text-3xl font-bold ${
                          darkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}>{experience.role}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                            darkMode 
                              ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 text-blue-300' 
                              : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
                          }`}>
                            <span className="font-semibold">{experience.company}</span>
                          </div>
                          <div className={`flex items-center gap-2 ${
                            darkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            <FaMapMarkerAlt className="text-blue-500" />
                            <span>{experience.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <div className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-full font-semibold shadow-lg transform hover:scale-105 transition-transform duration-300">
                      {experience.duration}
                    </div>
                    <div className={`flex items-center gap-2 text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <FaCalendarAlt className="text-blue-500" />
                      <span>Full-time Position</span>
                    </div>
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="mb-8">
                  <h4 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>Technologies Used</h4>
                  <div className="flex flex-wrap gap-3">
                    {experience.technologies.map((tech, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 backdrop-blur-sm ${
                          darkMode 
                            ? 'bg-gray-800/50 border-gray-700' 
                            : 'bg-white/80 border-gray-100'
                        }`}
                      >
                        <div className={tech.color}>{tech.icon}</div>
                        <span className={`font-medium ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>{tech.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Key Responsibilities Grid */}
            <div className="px-8 md:px-10 pb-10">
              <h4 className={`text-2xl font-bold mb-8 text-center ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Key <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Responsibilities</span>
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {experience.responsibilities.map((responsibility, index) => (
                  <div
                    key={index}
                    className={`group relative overflow-hidden backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border animate-slide-up ${
                      darkMode 
                        ? 'bg-gray-800/50 border-gray-700' 
                        : 'bg-white/90 border-gray-100'
                    }`}
                    style={{ 
                      animationDelay: `${index * 100}ms`,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(30px)'
                    }}
                    onMouseEnter={() => setActiveStat(index)}
                    onMouseLeave={() => setActiveStat(null)}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${
                      darkMode ? responsibility.darkColor : responsibility.color
                    } opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                    
                    <div className="relative z-10">
                      <div className={`mb-4 inline-flex p-3 rounded-xl bg-gradient-to-br ${
                        darkMode ? responsibility.darkColor : responsibility.color
                      } text-white transform group-hover:scale-110 transition-transform duration-500`}>
                        {responsibility.icon}
                      </div>
                      
                      <h5 className={`text-xl font-bold mb-3 ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>{responsibility.title}</h5>
                      <p className={`mb-4 ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>{responsibility.text}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className={`text-2xl font-bold bg-gradient-to-r ${
                          darkMode ? responsibility.darkColor : responsibility.color
                        } bg-clip-text text-transparent`}>
                          {responsibility.metrics[0]}
                        </div>
                        <div className={`text-sm ${
                          darkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          {responsibility.metrics[1]} {responsibility.metrics[2]}
                        </div>
                      </div>
                    </div>
                    
                    <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${
                      darkMode ? responsibility.darkColor : responsibility.color
                    } transition-all duration-500 group-hover:w-full`}></div>
                  </div>
                ))}
              </div>

              {/* Performance Stats */}
              <div className="mb-12">
                <h4 className={`text-2xl font-bold text-center mb-8 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  Performance <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Highlights</span>
                </h4>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className={`group relative overflow-hidden rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border ${
                        darkMode 
                          ? 'bg-gray-800/50 border-gray-700' 
                          : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'
                      }`}
                      onMouseEnter={() => setActiveStat(index)}
                      onMouseLeave={() => setActiveStat(null)}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${
                        darkMode ? stat.darkColor : stat.color
                      } opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                      
                      <div className="relative z-10 text-center">
                        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${
                          darkMode ? stat.darkColor : stat.color
                        } text-white mb-4`}>
                          {stat.icon}
                        </div>
                        
                        <div className={`text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-br ${
                          darkMode ? stat.darkColor : stat.color
                        } bg-clip-text text-transparent`}>
                          {stat.value}
                        </div>
                        <div className={`${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>{stat.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div className="mb-12">
                <h4 className={`text-2xl font-bold text-center mb-8 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  Career <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Timeline</span>
                </h4>
                
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 via-cyan-500 to-purple-500 rounded-full hidden md:block"></div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {experience.timeline.map((item, index) => (
                      <div
                        key={index}
                        className={`relative group ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12 md:col-start-2 md:row-start-${index + 1}'}`}
                      >
                        {/* Timeline dot */}
                        <div className={`absolute left-1/2 transform -translate-x-1/2 md:left-auto md:right-0 md:transform-none top-0 md:top-1/2 md:-translate-y-1/2 w-8 h-8 ${
                          darkMode ? 'bg-gray-800' : 'bg-white'
                        } border-4 border-blue-500 rounded-full z-10 group-hover:scale-125 transition-transform duration-300`}></div>
                        
                        <div className={`backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border ${
                          darkMode 
                            ? 'bg-gray-800/50 border-gray-700' 
                            : 'bg-white/90 border-gray-100'
                        }`}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`px-3 py-1 rounded-full ${
                              darkMode 
                                ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 text-blue-300' 
                                : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
                            }`}>
                              <span className="font-semibold">{item.year}</span>
                            </div>
                            <span className={`font-medium ${
                              darkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>{item.month}</span>
                          </div>
                          <p className={`font-medium ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>{item.event}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Achievements */}
              <div className={`rounded-2xl p-8 backdrop-blur-sm border ${
                darkMode 
                  ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-gray-700/30' 
                  : 'bg-gradient-to-r from-blue-50/80 to-cyan-50/80 border-blue-200/50'
              }`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl flex items-center justify-center">
                    <FaAward className="h-6 w-6 text-white" />
                  </div>
                  <h4 className={`text-2xl font-bold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Notable Achievements</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {experience.achievements.map((achievement, index) => (
                    <div key={index} className="flex items-start gap-3 group">
                      <div className="mt-1">
                        <FaStar className="h-5 w-5 text-yellow-500 group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <p className={`group-hover:opacity-100 transition-colors duration-300 ${
                        darkMode ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-700 group-hover:text-gray-900'
                      }`}>
                        {achievement}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Career Philosophy */}
        <div className="text-center">
          <div className={`inline-flex items-center gap-4 px-6 py-4 rounded-2xl border backdrop-blur-sm ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-gray-700/30' 
              : 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-blue-200/30'
          }`}>
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center">
              <FaLightbulb className="h-6 w-6 text-white" />
            </div>
            <div className="text-left">
              <p className={`text-lg font-semibold ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>
                "Proactive problem-solving and continuous learning are key to successful DevOps transformation"
              </p>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Focused on bridging development and operations through automation and collaboration
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes timeline-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        
        .animate-timeline-in {
          animation: timeline-in 0.6s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Experience;